import React, { useState, useCallback, useEffect } from "react";
import { MasterTable } from "../../components/_index";
import { Box, Stack, Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, TextField, Typography, Fade } from "@mui/material";
import Grid from "@mui/material/Grid2";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import { debounce } from "../../utils/debounce";
import { useDispatch } from "react-redux";
import moment from "moment";
import { ExcelReport } from "./ExcelExport";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import hrLoginStatusService from "../../services/hrLoginStatus.service";
import { getHrAdminAccountsUsers } from "../../services/user.service";
import { showToast } from "../../redux/Reducer/toastReducer";

const HrReports = () => {
    const { RangePicker } = DatePicker;
    const dispatch = useDispatch();
    const [data, setData] = useState([]);
    const [tableTotalCount, setTableCount] = useState(0);
    const [isAdmin, setIsAdmin] = useState(false);
    const [hrUsers, setHrUsers] = useState([]);
    const [openModal, setOpenModal] = useState(false);

    const [assignment, setAssignment] = useState({
        _id: "",
        date: null,
        assignedHrId: "",
        actualHrId: "",
        isEdit: false
    });

    const [queryParam, setQueryParam] = useState({
        search: "",
        page: 1,
        limit: 10,
        startDate: dayjs().startOf("month").format("DD-MM-YYYY"),
        endDate: dayjs().endOf("month").format("DD-MM-YYYY"),
    });

    useEffect(() => {
        const adminStatus = localStorage.getItem("isAdmin") === "true" || localStorage.getItem("isOverAllAdmin") === "true";
        setIsAdmin(adminStatus);
        if (adminStatus) {
            fetchHrUsers();
        }
    }, []);

    const fetchHrUsers = async () => {
        try {
            const res = await getHrAdminAccountsUsers();
            // Filter to only show users with the 'HR' role
            const filteredHrs = (res?.data || []).filter(user => user.role === "HR");
            setHrUsers(filteredHrs);
        } catch (error) {
            console.log("Error fetching HR users:", error);
        }
    };

    const GetHrLoginStatusData = async () => {
        try {
            const res = await hrLoginStatusService.getHrLoginStatusReport(queryParam);
            const modifiedData =
                res?.data.map((e, index) => ({
                    ...e,
                    siNo: (queryParam.page - 1) * queryParam.limit + index + 1,
                    formattedDate: moment(e.date).format("DD-MM-YYYY"),
                    actualPerson: e.name || "Pending",
                    assignedTo: e.assignedName || "Regular",
                })) || [];
            setTableCount(res?.totalCount || 0);
            setData(modifiedData);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        GetHrLoginStatusData();
    }, [
        queryParam.search,
        queryParam.limit,
        queryParam.page,
        queryParam.startDate,
        queryParam.endDate,
    ]);

    const columns = [
        { id: "siNo", label: "SI.No" },
        { id: "assignedTo", label: "Assigned To" },
        { id: "actualPerson", label: "Actual Responsible Person" },
        { id: "time", label: "Time of Approval" },
        { id: "formattedDate", label: "Date" },
    ];

    const tableOutputEvent = useCallback((params) => {
        setQueryParam((queryParams) => ({
            ...queryParams,
            ...params,
        }));
    }, []);

    const debouncedHandleSearch = useCallback(
        debounce((searchText) => {
            setQueryParam((queryParams) => ({
                ...queryParams,
                search: searchText,
                page: 1,
            }));
        }, 300),
        []
    );

    const handleSearch = (searchText) => {
        debouncedHandleSearch(searchText);
    };

    const handleOpenModal = () => {
        setAssignment({ _id: "", date: null, assignedHrId: "", actualHrId: "", isEdit: false });
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setAssignment({ _id: "", date: null, assignedHrId: "", actualHrId: "", isEdit: false });
    };

    const handleEdit = (row) => {
        if (!isAdmin) return;

        setAssignment({
            _id: row._id,
            date: dayjs(row.date),
            assignedHrId: String(row.assignedHrId || ""),
            actualHrId: String(row.hrId || ""),
            isEdit: true
        });
        setOpenModal(true);
    };

    const handleAssign = async () => {
        if (!assignment.date) {
            dispatch(showToast({ message: "Please select a date", type: "error" }));
            return;
        }

        try {
            const selectedAssignedHr = hrUsers.find(u => String(u._id) === String(assignment.assignedHrId));
            const selectedActualHr = hrUsers.find(u => String(u._id) === String(assignment.actualHrId));

            const payload = {
                _id: assignment._id,
                date: assignment.date.format("YYYY-MM-DD"),
                assignedHrId: assignment.assignedHrId,
                assignedName: selectedAssignedHr?.name || "",
                hrId: assignment.actualHrId,
                name: selectedActualHr?.name || "",
            };

            const res = await hrLoginStatusService.assignHrResponsibility(payload);
            if (!res.error && !res.data?.error) {
                dispatch(showToast({
                    message: assignment.isEdit ? "Assignment Updated Successfully" : "HR Assigned Successfully",
                    type: "success"
                }));
                handleCloseModal();
                GetHrLoginStatusData();
            } else {
                dispatch(showToast({ message: res.data?.message || res.message || "Failed to assign HR", type: "error" }));
            }
        } catch (error) {
            console.error("Error assigning HR:", error);
            const errorMessage = error?.data?.message || error?.message || "Failed to assign HR";
            dispatch(showToast({ message: errorMessage, type: "error" }));
        }
    };

    return (
        <div className="mainContent">
            <Grid container mb={2} spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 3 }}>
                    <Box className="SearchInputBoxContainer">
                        <SearchOutlinedIcon className="Search_Icon" />
                        <input
                            className="SearchInputBox"
                            placeholder="Search by name"
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                    </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 5 }}>
                    <RangePicker
                        value={[
                            queryParam.startDate
                                ? dayjs(queryParam.startDate, "DD-MM-YYYY")
                                : null,
                            queryParam.endDate
                                ? dayjs(queryParam.endDate, "DD-MM-YYYY")
                                : null,
                        ]}
                        onChange={(e) => {
                            setQueryParam({
                                ...queryParam,
                                startDate: e?.[0] ? dayjs(e?.[0]).format("DD-MM-YYYY") : null,
                                endDate: e?.[1] ? dayjs(e?.[1]).format("DD-MM-YYYY") : null,
                                page: 1,
                            });
                        }}
                        style={{
                            height: "38px",
                            width: "100%",
                        }}
                    />
                </Grid>

                {isAdmin && (
                    <Grid size={{ xs: 12, sm: 2 }}>
                        <Button
                            variant="contained"
                            onClick={handleOpenModal}
                            sx={{
                                backgroundColor: "#00675D",
                                "&:hover": { backgroundColor: "#00554d" },
                                width: "100%",
                                height: "38px",
                                textTransform: "none"
                            }}
                        >
                            Assign Person
                        </Button>
                    </Grid>
                )}
            </Grid>
            <MasterTable
                data={data}
                columns={columns}
                isServerSide={true}
                tableTotalCount={tableTotalCount}
                tableOutputEvent={tableOutputEvent}
                handleEdit={isAdmin ? handleEdit : null}
            />

            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>
                    {assignment.isEdit ? "Edit HR Assignment" : "Assign Responsible Person"}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} mt={1}>
                        <FormControl fullWidth>
                            <label style={{ marginBottom: '8px', display: 'block', fontWeight: 500 }}>Date</label>
                            <DatePicker
                                style={{ width: '100%', height: '45px' }}
                                placeholder="Select Date"
                                format="DD-MM-YYYY"
                                value={assignment.date}
                                onChange={(date) => setAssignment({ ...assignment, date })}
                            />
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id="assigned-hr-label">Assigned To</InputLabel>
                            <Select
                                labelId="assigned-hr-label"
                                value={assignment.assignedHrId}
                                label="Assigned To"
                                onChange={(e) => setAssignment({ ...assignment, assignedHrId: String(e.target.value) })}
                                sx={{ height: '45px' }}
                            >
                                <MenuItem value=""><em>None</em></MenuItem>
                                {hrUsers.map((user) => (
                                    <MenuItem key={String(user._id)} value={String(user._id)}>
                                        {user.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {assignment.isEdit && (
                            <FormControl fullWidth>
                                <InputLabel id="actual-hr-label">Actual Responsible Person</InputLabel>
                                <Select
                                    labelId="actual-hr-label"
                                    value={assignment.actualHrId}
                                    label="Actual Responsible Person"
                                    onChange={(e) => setAssignment({ ...assignment, actualHrId: String(e.target.value) })}
                                    sx={{ height: '45px' }}
                                >
                                    <MenuItem value=""><em>Pending / None</em></MenuItem>
                                    {hrUsers.map((user) => (
                                        <MenuItem key={String(user._id)} value={String(user._id)}>
                                            {user.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCloseModal} variant="outlined" color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAssign}
                        variant="contained"
                        sx={{
                            backgroundColor: "#00675D",
                            "&:hover": { backgroundColor: "#00554d" },
                        }}
                    >
                        {assignment.isEdit ? "Update" : "Assign"}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default HrReports;
